import {Divider, Stack, useMobileBreakpoint} from '@customink/pigment-react';
import PropTypes from 'prop-types';
import React, {useCallback, useEffect, useMemo} from 'react';
import ProductFeatureBadges from './components/Badges/ProductFeatureBadges';
import ProductPromoBadges from './components/Badges/ProductPromoBadges';
import ElevateSuperRush from './components/ElevateSuperRush';
import MinQty from './components/MinQty';
import OutOfStockBanner from './components/OutOfStockBanner';
import OutOfStockOverlay from './components/OutOfStockOverlay';
import ProductCardColorSwatches from './components/ProductCardColorSwatches';
import ProductCardContainer from './components/ProductCardContainer';
import ProductCardDetailsContainer from './components/ProductCardDetailsContainer';
import ProductCardImage from './components/ProductCardImage';
import ProductCardImageContainer from './components/ProductCardImageContainer';
import ProductCardPriceDetails from './components/ProductCardPriceDetails/ProductCardPriceDetails';
import ProductCardUnbundledPriceDetails from './components/ProductCardPriceDetails/ProductCardUnbundledPriceDetails';
import ProductCardRating from './components/ProductCardRating';
import ProductCardSizesInfo from './components/ProductCardSizesInfo';
import ProductCardTitle from './components/ProductCardTitle';
import useProductCardLink from './hooks/useProductCardLink';
import useProductColorData from './hooks/useProductColorData';
import {getMinimumDeliveryDays} from './utils/delivery';

export default function ProductCard({
  backColor,
  badges,
  containerProps = {},
  decorationMethod,
  defaultUnitPrices,
  displayAllSwatches = false,
  displayImageAsCover = false,
  filterColorId,
  footerContent,
  frontColor,
  generalComments,
  getProductImageSrcData,
  hasDirtyQuoteQuantity = false,
  hasSinglesEnabledColors,
  hideDetails,
  initialSelectedColorId,
  minQty,
  minQtyToggleSet,
  name,
  noQtyLimit,
  onSelectProduct,
  pricingQuantity,
  primaryCategoryId,
  productColors,
  productId,
  productPath,
  ratingCount,
  ratingScore,
  rushDeliveryTurntimes,
  showUnbundledPrice = false,
  sizes = [],
  specialty,
  status
}) {
  const isMobile = useMobileBreakpoint();
  const {colorData, onChangeColor} = useProductColorData({
    getProductImageSrcData,
    initialSelectedColorId
  });

  useEffect(() => {
    if (filterColorId) {
      onChangeColor(filterColorId, true);
    } else {
      onChangeColor(initialSelectedColorId);
    }
  }, [filterColorId, initialSelectedColorId, onChangeColor]);

  const linkHref = useProductCardLink({
    badges,
    initialSelectedColorId,
    minQtyToggleSet,
    productPath,
    selectedColorId: colorData.colorId,
    showUnbundledPrice
  });

  const minDeliveryDays = useMemo(
    () => getMinimumDeliveryDays(rushDeliveryTurntimes),
    [rushDeliveryTurntimes]
  );

  const onSwatchClick = useCallback(
    (swatch) => {
      onChangeColor(Number(swatch.value), true);
    },
    [onChangeColor]
  );

  const onSwatchHover = useCallback(
    (swatch) => {
      onChangeColor(Number(swatch.value), true);
    },
    [onChangeColor]
  );

  const onProductCardClick = useCallback(
    (event) => {
      onSelectProduct?.();

      if (!event.ctrlKey && !event.metaKey) {
        // Preserve ctrl+click or cmd+click for opening in new tab
        window.location.href = linkHref;
      }
    },
    [linkHref, onSelectProduct]
  );

  const isOutOfStock = status === 'inactive';

  // Memoize the swatches component to prevent unnecessary re-renders
  const swatchesComponent = useMemo(
    () => (
      <Stack direction="row" alignItems="flex-start">
        <ProductCardColorSwatches
          onSwatchClick={onSwatchClick}
          onSwatchHover={onSwatchHover}
          productColors={productColors}
          displayAllSwatches={displayAllSwatches}
        />
      </Stack>
    ),
    [onSwatchClick, onSwatchHover, productColors, displayAllSwatches]
  );

  const hasRating = ratingScore > 0;
  const hasSizes = sizes.length > 1;
  return (
    <ProductCardContainer
      isMobile={isMobile}
      data-testid="product"
      data-style-id={productId}
      onClick={onProductCardClick}
      position="relative"
      {...containerProps}>
      {isOutOfStock && <OutOfStockOverlay />}
      {/* Product Image */}
      <ProductCardImageContainer
        data-testid="product-image-container"
        href={linkHref}
        aria-label={`View details for ${name}`}>
        {isOutOfStock && <OutOfStockBanner />}
        <ProductCardImage
          data-testid="product-image"
          cover={displayImageAsCover}
          src={colorData.image.src}
          srcSet={colorData.image.srcset}
          alt={name}
          loading="lazy"
          itemProp="image"
          id={`product-image-${productId}`}
        />

        {/* Badges */}
        <ProductPromoBadges badges={badges} compact={isMobile} />
        <ProductFeatureBadges
          badges={badges}
          compact={isMobile}
          productName={name}
        />
      </ProductCardImageContainer>

      {/* Product Details */}
      <ProductCardDetailsContainer isMobile={isMobile}>
        {/* Conditionally render components based on viewport */}
        {isMobile && (
          <ProductCardTitle
            compact
            data-testid="product-title"
            aria-label={`View details for ${name}`}
            href={linkHref}
            itemProp="name">
            {name}
          </ProductCardTitle>
        )}

        {/* Limited number of swatches */}
        {!displayAllSwatches && !hideDetails && swatchesComponent}

        {/* Product Title - desktop only (after swatches) */}
        {!isMobile && (
          <ProductCardTitle
            data-testid="product-title"
            aria-label={`View details for ${name}`}
            href={linkHref}
            itemProp="name">
            {name}
          </ProductCardTitle>
        )}

        {/* Rating and Sizes  */}
        {(hasRating || hasSizes) && !hideDetails && (
          <Stack
            direction="row"
            gap="0.5rem"
            alignItems="center"
            data-testid="product-rating-and-sizes">
            {hasRating && (
              <ProductCardRating
                count={ratingCount}
                score={ratingScore}
                generalComments={generalComments}
                name={name}
              />
            )}

            {hasRating && hasSizes && (
              <Divider orientation="vertical" flexItem />
            )}

            {hasSizes && (
              <ProductCardSizesInfo productId={productId} sizes={sizes} />
            )}
          </Stack>
        )}

        {/* Price and Details */}
        {!hideDetails &&
          (showUnbundledPrice ? (
            <ProductCardUnbundledPriceDetails
              decorationMethod={decorationMethod}
              defaultUnitPrices={defaultUnitPrices}
              quantity={pricingQuantity}
            />
          ) : (
            <ProductCardPriceDetails
              backColor={backColor}
              defaultUnitPrices={defaultUnitPrices}
              frontColor={frontColor}
              isMobile={isMobile}
              minQty={minQty}
              productId={colorData.colorId || productId}
              styleId={productId}
              name={name}
              quantity={pricingQuantity}
              hasSinglesEnabledColors={hasSinglesEnabledColors}
              hasDirtyQuoteQuantity={hasDirtyQuoteQuantity}
              specialty={specialty}
              productColors={productColors}
              primaryCategoryId={primaryCategoryId}
            />
          ))}
        {/* Delivery Rush Badge */}
        {minDeliveryDays && !hideDetails && (
          <ElevateSuperRush deliveryRushDays={minDeliveryDays} />
        )}

        {/* Minimum qty */}
        {!hideDetails && <MinQty minQty={noQtyLimit ? null : minQty} />}

        {/* All Swatches */}
        {displayAllSwatches && !hideDetails && swatchesComponent}
        {!hideDetails && footerContent}
      </ProductCardDetailsContainer>
    </ProductCardContainer>
  );
}

ProductCard.propTypes = {
  backColor: PropTypes.number.isRequired,
  badges: PropTypes.array,
  categoryId: PropTypes.number.isRequired,
  containerProps: PropTypes.object,
  defaultUnitPrices: PropTypes.arrayOf(PropTypes.object).isRequired,
  displayAllSwatches: PropTypes.bool,
  displayImageAsCover: PropTypes.bool,
  filterColorId: PropTypes.number,
  frontColor: PropTypes.number.isRequired,
  generalComments: PropTypes.string,
  getProductImageSrcData: PropTypes.func,
  hasSinglesEnabledColors: PropTypes.bool.isRequired,
  hideDetails: PropTypes.bool,
  initialSelectedColorId: PropTypes.number,
  minQty: PropTypes.number,
  minQtyToggleSet: PropTypes.bool,
  name: PropTypes.string.isRequired,
  noQtyLimit: PropTypes.bool,
  onSelectProduct: PropTypes.func,
  pricingQuantity: PropTypes.number.isRequired,
  primaryCategoryId: PropTypes.number.isRequired,
  productColors: PropTypes.array.isRequired,
  productId: PropTypes.number.isRequired,
  productPath: PropTypes.string.isRequired,
  ratingCount: PropTypes.number,
  ratingScore: PropTypes.number,
  rushDeliveryTurntimes: PropTypes.array,
  sizes: PropTypes.array,
  specialty: PropTypes.bool.isRequired,
  status: PropTypes.string.isRequired
};
